//#region IMPORTS
import {
  createInstoreTypeAsync,
  updateInstoreTypeAsync,
} from "../../features/instoreTypes/instoreTypesSlice";
import {
  storeFormatSelectors,
  fetchStoreFormatsAsync,
} from "../../features/storeFormats/storeFormatSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Controller, useForm } from "react-hook-form";
import { StoreFormat } from "../../models/storeFormat";
import { InstoreType } from "../../models/instoreType";
import { useEffect } from "react";
import { toast } from "react-toastify";
import InstoreTypeFormStoreFormatSelector from "./InstoreTypeFormStoreFormatSelector";
import SubmitButton from "../genericButtons/submitButton/SubmitButton";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  instoreType?: InstoreType;
  cancelCUD: () => void;
  disabled?: boolean;
}
//#endregion

export default function InstoreTypeForm({
  instoreType,
  cancelCUD,
  disabled = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(({ instoreTypes }) => instoreTypes);

  const { storeFormats_loaded, loading: storeFormatsLoading } = useAppSelector(
    ({ storeFormats }) => storeFormats
  );
  const storeFormats = useAppSelector(storeFormatSelectors.selectAll);

  // UseForm
  const {
    reset,
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm<InstoreType>({
    mode: "all",
    defaultValues: {
      store_formats: [],
    },
  });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchStoreFormatsAsync());
    };
    !storeFormats_loaded && fetchData();
  }, [storeFormats_loaded, dispatch]);

  useEffect(() => {
    if (instoreType) {
      reset(instoreType);
    }
  }, [instoreType, setValue, reset]);

  // Submit form
  async function submitForm(data: InstoreType) {
    const store_format_ids =
      data.store_formats.map((storeFormat) => storeFormat.id) ?? [];
    try {
      if (instoreType) {
        await dispatch(
          updateInstoreTypeAsync({
            id: instoreType.id,
            values: { name: data.name, store_format_ids },
          })
        );
      } else {
        await dispatch(
          createInstoreTypeAsync({
            values: { name: data.name, store_format_ids },
          })
        );
      }
    } catch (error) {
      toast.error(`${error}`);
    }
    cancelCUD();
  }
  //#endregion
  // Watch values
  const [storeFormatsValue] = watch(["store_formats"]);

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          {/* HEADER */}
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {instoreType
              ? `Update type: ${instoreType.name}`
              : "Create new instore type"}
          </h2>

          {/* BODY */}
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* name */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="name"
                className={classNames(
                  errors.name ? "text-red-900" : "text-gray-900",
                  "block text-sm font-medium leading-6"
                )}
              >
                {errors.name ? errors?.name?.message : "type name"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. My first type"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading || storeFormatsLoading}
                  type="text"
                  {...register("name", {
                    required: "Name is required.",
                  })}
                />
              </div>
            </fieldset>

            {/* store_format_names */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="store_formats"
                className="text-gray-900 block text-sm font-medium leading-6"
              >
                store formats
              </label>
              <div className="relative mt-2">
                <Controller
                  control={control}
                  name="store_formats"
                  render={({ field }) => (
                    <InstoreTypeFormStoreFormatSelector
                      disabled={
                        loading ||
                        isSubmitting ||
                        !storeFormats_loaded ||
                        storeFormatsLoading
                      }
                      items={storeFormats}
                      selectedValues={storeFormatsValue || []}
                      onChangeHandler={(values: StoreFormat[]) => {
                        field.onChange(values);
                      }}
                      tooltipId="47e70929-81e3-4a17-8ece-f7140f872853"
                    />
                  )}
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          onClick={cancelCUD}
          type="button"
          disabled={disabled || loading}
          className={classNames(
            disabled
              ? "text-gray-300 hover:cursor-not-allowed"
              : "text-rose-700",
            "text-sm font-semibold leading-6"
          )}
        >
          Cancel
        </button>

        <SubmitButton
          conditionals={[isValid]}
          isSubmitting={isSubmitting}
          disabled={disabled || loading}
        />
      </div>
    </form>
  );
}
